import {
  nikkeCatalogSearchText,
  normalizeNikkeLookupKey,
  resolveNikkeInCatalog,
} from '../../nikke-catalog/domain/nikkeCatalog.js';

const MAX_ALIAS_COUNT = 2;
const IDENTIFIER_FIELDS = [
  ['nikke_id', 'nikkeId'],
  ['resource_id', 'resourceId'],
  ['game_character_id', 'gameCharacterId'],
  ['name_code', 'nameCode'],
];

const text = (value) => String(value ?? '').trim();

function uniqueText(values = []) {
  const seen = new Set();
  const result = [];
  for (const value of values) {
    const next = text(value);
    const key = normalizeNikkeLookupKey(next);
    if (!next || seen.has(key)) continue;
    seen.add(key);
    result.push(next);
  }
  return result;
}

function itemIdentifierValue(item, key) {
  return text(item?.[key] ?? item?.[key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)]);
}

export function aliasStoragePolicy() {
  return {
    kind: 'nikke_db_alias1_alias2',
    maxAliases: MAX_ALIAS_COUNT,
    normalizedAliasTableAvailable: false,
    migrationRequiredForMoreAliases: true,
  };
}

export function nikkeManagementAliases(item = {}) {
  return uniqueText(item.aliases || [item.alias1, item.alias2]);
}

export function buildNikkeDbSearchResults(catalogResult = {}, query = '', { limit = Number.POSITIVE_INFINITY } = {}) {
  const items = Array.isArray(catalogResult.items)
    ? catalogResult.items
    : Array.isArray(catalogResult.rows)
      ? catalogResult.rows
      : [];
  const lookup = catalogResult.lookup || catalogResult;
  const needle = normalizeNikkeLookupKey(query);
  const exact = needle ? resolveNikkeInCatalog(lookup, query) : null;
  const exactId = exact?.status === 'resolved' ? exact.item?.nikkeId : '';
  const ambiguousIds = exact?.status === 'ambiguous'
    ? new Set((exact.matches || []).map((item) => item.nikkeId))
    : new Set();

  const ranked = [];
  for (const item of items) {
    if (!item?.nikkeId) continue;
    if (!needle) {
      ranked.push({ item, score: 1, position: ranked.length });
      continue;
    }

    const searchText = normalizeNikkeLookupKey(nikkeCatalogSearchText(item));
    const identityValues = [
      item.nikkeId,
      item.resourceId,
      item.gameCharacterId,
      item.nameCode,
      item.name,
      item.officialName,
      ...nikkeManagementAliases(item),
      ...(Array.isArray(item.sourceIdentifiers) ? item.sourceIdentifiers : []),
    ].map(normalizeNikkeLookupKey).filter(Boolean);
    const startsWith = identityValues.some((value) => value.startsWith(needle));
    const exactField = identityValues.includes(needle);
    const includes = searchText.includes(needle);
    if (!includes && !startsWith && item.nikkeId !== exactId && !ambiguousIds.has(item.nikkeId)) continue;

    const offset = includes ? searchText.indexOf(needle) : 9999;
    let score = includes ? 10 : 0;
    if (startsWith) score += 30;
    if (exactField) score += 60;
    if (ambiguousIds.has(item.nikkeId)) score += 70;
    if (item.nikkeId === exactId) score += 100;
    ranked.push({ item, score, position: offset });
  }

  const deduped = new Map();
  for (const entry of ranked.sort((left, right) => (
    right.score - left.score
    || left.position - right.position
    || Number(left.item.displayOrder || 0) - Number(right.item.displayOrder || 0)
    || Number(left.item.resourceId || 0) - Number(right.item.resourceId || 0)
  ))) {
    if (!deduped.has(entry.item.nikkeId)) deduped.set(entry.item.nikkeId, entry.item);
    if (deduped.size >= limit) break;
  }
  return [...deduped.values()];
}

export function validateNikkeAliasDraft({ catalog, targetNikkeId, aliases = [] } = {}) {
  const cleaned = uniqueText(aliases);
  const errors = [];
  const conflicts = [];
  const rawKeys = aliases.map((alias) => normalizeNikkeLookupKey(alias)).filter(Boolean);
  if (new Set(rawKeys).size !== rawKeys.length) {
    errors.push('같은 별명이 중복 입력되어 있습니다.');
  }
  if (cleaned.length > MAX_ALIAS_COUNT) {
    errors.push(`현재 DB 스키마는 별명을 최대 ${MAX_ALIAS_COUNT}개까지 저장할 수 있습니다.`);
  }

  for (const alias of cleaned) {
    const resolved = resolveNikkeInCatalog(catalog, alias);
    const conflictItems = resolved.status === 'resolved'
      ? [resolved.item].filter((item) => item?.nikkeId && item.nikkeId !== targetNikkeId)
      : resolved.status === 'ambiguous'
        ? (resolved.matches || []).filter((item) => item?.nikkeId && item.nikkeId !== targetNikkeId)
        : [];
    if (conflictItems.length) {
      conflicts.push({
        alias,
        matches: conflictItems.map((item) => ({
          nikkeId: item.nikkeId,
          name: item.name || item.officialName || item.nikkeId,
          resourceId: item.resourceId,
        })),
      });
    }
  }

  if (conflicts.length && !errors.length) {
    errors.push(`${conflicts[0].alias} 별명은 이미 ${conflicts[0].matches[0]?.name || '다른 니케'}에 연결되어 있습니다.`);
  }

  return {
    ok: errors.length === 0,
    aliases: cleaned,
    alias1: cleaned[0] || null,
    alias2: cleaned[1] || null,
    conflicts,
    errors,
    policy: aliasStoragePolicy(),
  };
}

export function validateNikkeIdentifierConflicts(items = []) {
  const registry = new Map();
  const add = (type, value, item) => {
    const key = normalizeNikkeLookupKey(value);
    if (!key || !item?.nikkeId) return;
    const registryKey = `${type}:${key}`;
    if (!registry.has(registryKey)) registry.set(registryKey, { type, value: text(value), items: [] });
    const entry = registry.get(registryKey);
    if (!entry.items.some((existing) => existing.nikkeId === item.nikkeId)) {
      entry.items.push({
        nikkeId: item.nikkeId,
        name: item.name || item.officialName || item.nikkeId,
        resourceId: item.resourceId,
      });
    }
  };

  for (const item of items) {
    for (const [type, key] of IDENTIFIER_FIELDS) add(type, itemIdentifierValue(item, key), item);
    for (const value of item.sourceIdentifiers || []) add('source_identifier', value, item);
  }

  return [...registry.values()]
    .filter((entry) => entry.items.length > 1)
    .sort((left, right) => left.type.localeCompare(right.type) || left.value.localeCompare(right.value));
}

export function conflictsForNikke(items = [], targetNikkeId = '') {
  return validateNikkeIdentifierConflicts(items)
    .filter((entry) => entry.items.some((item) => item.nikkeId === targetNikkeId));
}

export function buildNikkeDisplayDraft(item = {}) {
  return {
    nikkeId: text(item.nikkeId),
    resourceId: Number(item.resourceId || 0),
    name: text(item.name || item.officialName || item.nikkeId),
    aliases: nikkeManagementAliases(item).slice(0, MAX_ALIAS_COUNT),
    element: text(item.element),
    burst: text(item.burst),
    imageUrl: text(item.imageUrl),
    active: item.active !== false,
  };
}

export function buildNikkeDisplayDiff(item = {}, draft = {}) {
  const currentAliases = nikkeManagementAliases(item).slice(0, MAX_ALIAS_COUNT);
  const nextAliases = uniqueText(draft.aliases).slice(0, MAX_ALIAS_COUNT);
  const fields = [
    ['name', '표시명', text(item.name || item.officialName || item.nikkeId), text(draft.name)],
    ['alias1', '별명 1', currentAliases[0] || '', nextAliases[0] || ''],
    ['alias2', '별명 2', currentAliases[1] || '', nextAliases[1] || ''],
    ['element', '표시 속성', text(item.element), text(draft.element)],
    ['burst', '표시 버스트', text(item.burst), text(draft.burst)],
    ['imageUrl', '이미지 URL', text(item.imageUrl), text(draft.imageUrl)],
    ['active', '활성 상태', item.active !== false ? 'active' : 'inactive', draft.active !== false ? 'active' : 'inactive'],
  ];
  return fields
    .filter(([, , before, after]) => before !== after)
    .map(([key, label, before, after]) => ({ key, label, before, after }));
}

export function buildNikkeDisplayUpsertPayload(item = {}, draft = {}) {
  const aliasValidation = validateNikkeAliasDraft({
    catalog: { byKey: new Map(), ambiguousKeys: new Map() },
    targetNikkeId: text(item.nikkeId),
    aliases: draft.aliases,
  });
  const resourceId = Number(draft.resourceId || item.resourceId || 0);
  const nikkeId = text(draft.nikkeId || item.nikkeId);
  if (!nikkeId || !resourceId) throw new Error('저장할 canonical NIKKE 식별자를 확인하지 못했습니다.');
  const name = text(draft.name || item.name || item.officialName || nikkeId);
  const imageUrl = text(draft.imageUrl || item.imageUrl);
  if (!name) throw new Error('표시명을 입력해주세요.');
  if (!imageUrl) throw new Error('이미지 URL을 확인하지 못했습니다.');
  if (aliasValidation.aliases.length > MAX_ALIAS_COUNT) throw new Error(aliasValidation.errors[0]);
  return {
    nikke_id: nikkeId,
    resource_id: resourceId,
    name,
    alias1: aliasValidation.alias1,
    alias2: aliasValidation.alias2,
    element: text(draft.element || item.element) || null,
    burst: text(draft.burst || item.burst) || null,
    image_url: imageUrl,
    active: draft.active !== false,
    raw: {
      source: 'platform_nikke_db_management',
      updatedFields: buildNikkeDisplayDiff(item, draft).map((field) => field.key),
    },
  };
}

export function summarizeExternalMasterPreview(preview = {}) {
  const payload = preview && typeof preview === 'object' && !Array.isArray(preview) ? preview : {};
  const changed = Array.isArray(payload.changeDetails) ? payload.changeDetails : [];
  const updated = Array.isArray(payload.updatedCharacters) ? payload.updatedCharacters : [];
  const inserted = Array.isArray(payload.insertedCharacters) ? payload.insertedCharacters : [];
  return {
    hasChanges: Boolean(changed.length || updated.length || inserted.length),
    changed,
    inserted,
    updated,
    complete: payload.complete === true,
    warnings: Array.isArray(payload.warnings) ? payload.warnings : [],
  };
}
